import type { BossDef } from './boss-base';
import { COLORS } from '@/config/colors';

/**
 * B11 Avatar data: the five-phase BossDef (one element per HP band) plus the
 * per-mode signature tuning. The behavior lives in `avatar.ts`; this file is
 * pure data so the class stays under the 300-line cap. docs/02 Elementals.
 * Signatures: each mode's element signature (wall / undertow / horizon /
 * slam / gust) + that mode's first pattern; the second unlocks at tier 2.
 */
export const AVATAR: BossDef = {
  id: 'avatar',
  nameKey: 'boss.avatar',
  tier: 5,
  finale: true,
  minRoom: 5,
  baseHp: 2200,
  radius: 50,
  bodyKey: 'boss/avatar/body',
  movement: 'orbit',
  contactDamage: 2,
  phases: [
    { // Mode 0 — FIRE
      hpPct: 1.0,
      patterns: [
        {
          type: 'aimed', bullet: 'bullet/enemy/fire', count: 3, spreadDeg: 24,
          interval: 2.4, telegraph: 0.4, speed: 250, radius: 11, damage: 1,
        },
        {
          type: 'radial', bullet: 'bullet/enemy/fire', count: 10, interval: 3.0,
          spinDeg: 18, speed: 140, radius: 11, damage: 1,
          unlockTier: 2,
        },
      ],
    },
    { // Mode 1 — WATER
      hpPct: 0.8,
      patterns: [
        {
          type: 'radial', bullet: 'bullet/enemy/ball', count: 16, interval: 3.2,
          spinDeg: 11, speed: 110, radius: 10, damage: 1,
        },
        {
          type: 'aimed', bullet: 'bullet/enemy/ball', count: 3, spreadDeg: 14,
          interval: 2.6, telegraph: 0.4, speed: 270, radius: 10, damage: 1,
          unlockTier: 2,
        },
      ],
    },
    { // Mode 2 — VOID
      hpPct: 0.6,
      patterns: [
        {
          type: 'radial', bullet: 'bullet/enemy/void', count: 12, interval: 2.4,
          spinDeg: -15, speed: 130, radius: 10, damage: 1,
        },
        {
          type: 'spiral', bullet: 'bullet/enemy/void', arms: 3, interval: 0.13,
          angularSpeed: 85, speed: 150, radius: 9, damage: 1,
          unlockTier: 2,
        },
      ],
    },
    { // Mode 3 — EARTH
      hpPct: 0.4,
      patterns: [
        {
          type: 'radial', bullet: 'bullet/enemy/rock', count: 11, interval: 3.0,
          spinDeg: 18, speed: 135, radius: 11, damage: 1,
        },
        {
          type: 'aimed', bullet: 'bullet/enemy/rock', count: 3, spreadDeg: 24,
          interval: 2.6, telegraph: 0.45, speed: 215, radius: 11, damage: 1,
          unlockTier: 2,
        },
      ],
    },
    { // Mode 4 — AIR (rage)
      hpPct: 0.2,
      speedMul: 1.3,
      patterns: [
        {
          type: 'spiral', bullet: 'bullet/enemy/wind', arms: 4, interval: 0.11,
          angularSpeed: 105, speed: 160, radius: 9, damage: 1,
        },
        {
          type: 'aimed', bullet: 'bullet/enemy/wind', count: 5, spreadDeg: 44,
          interval: 2.2, telegraph: 0.35, speed: 250, radius: 10, damage: 1,
          unlockTier: 2,
        },
      ],
    },
  ],
  evolve: [
    {
      // Elemental halo: a slow heavy ring under every mode
      tier: 3,
      pattern: {
        type: 'radial', bullet: 'bullet/enemy/ring', count: 10, interval: 4.0,
        spinDeg: 18, speed: 120, radius: 11, damage: 2,
      },
    },
    {
      // Convergence: from the void mode on, a double void fan joins in
      tier: 4,
      fromPhase: 2,
      pattern: {
        type: 'aimed', bullet: 'bullet/enemy/void', count: 3, spreadDeg: 20,
        interval: 3.4, telegraph: 0.45, burst: 2, burstGap: 0.3,
        speed: 260, radius: 10, damage: 1,
      },
    },
  ],
};

// Per-mode tuning (strengthened single-signature pulls from each Elemental)
export const AV = {
  wallInterval: 8.5, // fire: flame wall sweep (spans the REAL arena — X3)
  wallInset: 90, // starts this far inside the arena walls
  wallGap: 110,
  wallSpeed: 155,
  burstSpeed: { water: 175, void: 200, earth: 165 },
  rainRocks: 7,
  rainSpeed: 150,
  vortexInterval: 9, // water: undertow pull
  vortexWindup: 0.8,
  vortexTime: 2.6,
  vortexPull: 230,
  vortexRange: 720,
  shieldOpen: 4.0, // void: event horizon cycle
  shieldClosed: 2.4,
  spitCap: 20,
  slamInterval: 6.0, // earth: seismic slam (no invuln gate — finale)
  slamWindup: 0.9,
  crackLength: 520, // floor fissures before the slam
  gustInterval: 6.0, // air: shove + tornadoes
  gustWindup: 0.8,
  gustTime: 1.4,
  gustPush: 240,
} as const;

export const MODE_TINT = [
  COLORS.dangerOrange, COLORS.abyssGlow, COLORS.voidGlow,
  COLORS.terraStone, COLORS.galeWind,
] as const;
